import { LabLocalStage, LabPrivacyCategory, resolveLabPrivacyCategory } from './labSdk.types'

/**
 * What the running stage is doing right now, named after the stage on the run bar: `Detect · license plates`.
 * `key` sits under `photo_studio_editing:lab.progress_step`.
 */
export interface LabProgressStep {
  key: string
  params?: { step: number, total: number }
}

/** The steps before the order runs (and the download after it). The generic Prepare is left out: its segment says it */
const LOCAL_STEPS = new Set<string>([LabLocalStage.UPLOAD, LabLocalStage.UPLOAD_MASK, LabLocalStage.QUEUED, LabLocalStage.EXPORT])

/** The privacy detection's concepts (lab-back `DEFAULT_CATEGORIES`); any other prompt (declutter's obstructions) is "objects" */
const DETECTION_CONCEPTS: Record<LabPrivacyCategory, string> = {
  'human face': 'faces',
  'license plate': 'license_plates',
  'photo or frame': 'frames',
}

/** The workers' intra-op milestones (lab-back `pipeline_edit.py`), relayed verbatim as the job's stage */
const MILESTONES: Array<[RegExp, string]> = [
  [/^develop/, 'develop'],
  [/^hdr/, 'hdr'],
  [/^perspective/, 'perspective'],
  [/^sky/, 'sky'],
  [/^composit/, 'composite'],
  [/^tonecraft/, 'tone'],
  [/^(privacy )?blur/, 'privacy_blur'],
  [/^room/, 'room'],
  [/^window/, 'windows'],
]

const DETECTION = /^(?:sam3|detect):\s*(.+)$/
const SAMPLING = /^sampling\s+(\d+)\s*\/\s*(\d+)/

/**
 * The sub-step a Lab label names, or null for a bare stage name (`edit`, `gen`, `fal-1`) or a label this build does
 * not know — never shown raw. Detection reports its concept while it looks (`sam3: human face`) and once found
 * (`detect: license plate`); a generation counts its steps (`sampling 5/8`).
 */
export const resolveLabProgressStep = (label?: string): LabProgressStep | null => {
  const lower = label?.trim().toLowerCase()
  if (!lower) return null
  if (LOCAL_STEPS.has(lower)) return { key: lower }
  const detection = DETECTION.exec(lower)
  if (detection) {
    const category = resolveLabPrivacyCategory(detection[1])
    return { key: category ? DETECTION_CONCEPTS[category] : 'objects' }
  }
  const sampling = SAMPLING.exec(lower)
  if (sampling) return { key: 'refining', params: { step: Number(sampling[1]), total: Number(sampling[2]) } }
  const milestone = MILESTONES.find(([pattern]) => pattern.test(lower))
  return milestone ? { key: milestone[1] } : null
}

/** The edit worker reports this once it has straightened the photo — the cue for the straighten-grid scene */
export const isLabPerspectiveStep = (label?: string) => resolveLabProgressStep(label)?.key === 'perspective'
